<template>
  <section v-if="info" class="workout-recovery mb-4" aria-label="Offenes Training">
    <button type="button" class="recovery-notice" aria-haspopup="dialog" @click="open">
      <span class="recovery-notice-copy">
        <span class="recovery-eyebrow">{{ pendingSave ? 'Speicherung noch offen' : 'Noch nicht gespeichert' }}</span>
        <strong>Dein Training wartet auf dich</strong>
        <span class="recovery-plan-name">{{ info.title }}</span>
        <span class="recovery-notice-meta">{{ dateLabel }} <span aria-hidden="true">·</span> {{ setLabel }}</span>
      </span>
      <span class="recovery-notice-action">
        Prüfen <v-icon color="#a8efca" size="19">mdi-arrow-right</v-icon>
      </span>
    </button>

    <v-dialog v-model="dialog" max-width="460" dark content-class="recovery-dialog-overlay" :persistent="busy">
      <v-card class="recovery-dialog" role="document" aria-labelledby="recovery-dialog-title">
        <div class="recovery-dialog-top">
          <div class="recovery-eyebrow">{{ confirmDiscard ? 'Training entfernen' : pendingSave ? 'Speicherung noch offen' : 'Noch nicht gespeichert' }}</div>
          <v-btn icon :disabled="busy" aria-label="Hinweis schliessen" class="recovery-close" width="36" height="36" @click="dialog = false"><v-icon size="22">mdi-close</v-icon></v-btn>
        </div>
        <template v-if="confirmDiscard">
          <h2 id="recovery-dialog-title" class="recovery-title">Wirklich verwerfen?</h2>
          <p class="recovery-description">Deine erfassten Sätze werden von diesem Gerät entfernt. Das kannst du nicht rückgängig machen.</p>
        </template>
        <template v-else>
          <h2 id="recovery-dialog-title" class="recovery-title">{{ pendingSave ? 'Ein Schritt fehlt noch' : 'Training fertig?' }}</h2>
          <p class="recovery-description">{{ pendingSave ? 'Die Speicherung wurde noch nicht bestätigt. Prüfe den Status und versuche es bei Bedarf erneut.' : 'Deine Sätze sind noch da. Prüfe kurz Datum und Dauer, bevor du dein Training speicherst.' }}</p>
        </template>

        <div class="recovery-workout">
          <div class="recovery-workout-label">Dein Training</div>
          <h3>{{ info.title }}</h3>
          <div class="recovery-workout-facts">
            <span><v-icon size="16" color="#9dafaa">mdi-calendar-outline</v-icon>{{ dateLabel }}</span>
            <span><v-icon size="16" color="#9dafaa">mdi-check-circle-outline</v-icon>{{ setLabel }} erfasst</span>
          </div>
        </div>

        <div class="recovery-actions">
          <template v-if="confirmDiscard">
            <v-btn block height="52" depressed color="#edb0a8" class="recovery-primary" :loading="busy" @click="discard">Training verwerfen</v-btn>
            <v-btn block height="48" outlined class="recovery-secondary" :disabled="busy" @click="confirmDiscard = false">Training behalten</v-btn>
          </template>
          <template v-else>
            <v-btn block height="52" depressed color="#8ce7bb" class="recovery-primary" :loading="busy" @click="review">
              {{ pendingSave ? 'Speicherung prüfen' : 'Prüfen & speichern' }}<v-icon right size="20">mdi-arrow-right</v-icon>
            </v-btn>
            <v-btn v-if="!pendingSave" block height="48" outlined class="recovery-secondary" :disabled="busy" @click="resume">
              <v-icon left size="19">mdi-play-outline</v-icon>Training fortsetzen
            </v-btn>
            <div class="recovery-quiet-actions">
              <v-btn text height="44" :disabled="busy" @click="dialog = false">Später</v-btn>
              <v-btn v-if="!pendingSave" text height="44" :disabled="busy" @click="confirmDiscard = true">Verwerfen</v-btn>
            </div>
          </template>
        </div>
        <v-alert v-if="error" dense text type="error" role="alert" class="mt-3 mb-0">{{ error }}</v-alert>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import moment from "moment"
import userService from "@/services/userService"
import trainingLiveService from "@/services/trainingLiveService"
import { workoutRecoveryInfo } from "@/services/workoutRecovery"

export default {
  name: "WorkoutRecoveryNotice",
  data: () => ({ now: Date.now(), ready: false, dialog: false, forceReview: false, confirmDiscard: false, busy: false, error: "", interval: null }),
  computed: {
    state() { return this.$store.state.runWorkoutStore },
    info() {
      // Depend on the authenticated store as well as localStorage's user identity.
      const user = this.$store.state.userStore.user
      const requested = this.$route.query.recover === this.state.liveSession?.clientSessionId && Boolean(this.$route.query.recover)
      return this.ready && user ? workoutRecoveryInfo(this.state, userService.getUserId(), this.now, this.forceReview || requested) : null
    },
    dateLabel() { return this.info?.startedAt ? moment(this.info.startedAt).format("D. MMMM YYYY") : "Datum bitte prüfen" },
    setLabel() { return this.info?.sets === 1 ? "1 Satz" : `${this.info?.sets || 0} Sätze` },
    pendingSave() { return Boolean(this.state.liveSession?.pendingSave) },
  },
  watch: {
    dialog(value) { if (!value) this.forceReview = false },
    info(value) {
      if (!value) this.dialog = false
      else this.offerReminder()
    },
    "$route.query.recover"() { this.refresh() },
  },
  methods: {
    refresh() {
      if (document.visibilityState === "hidden" || !this.ready) return
      trainingLiveService.ensureSession()
      this.now = Date.now()
      this.offerReminder()
    },
    offerReminder() {
      if (!this.info || this.dialog || this.busy) return
      const requested = this.$route.query.recover === this.info.clientSessionId
      const promptedAt = this.state.liveSession?.recoveryPromptedAt
      if (requested || !promptedAt || !moment(promptedAt).isSame(this.now, "day")) this.open(requested)
      if (requested) {
        const query = { ...this.$route.query }
        delete query.recover
        this.$router.replace({ query }).catch(() => {})
      }
    },
    open(force = false) {
      this.forceReview = force === true
      if (!this.info) return
      this.confirmDiscard = false
      this.error = ""
      this.dialog = true
      this.$store.commit("runWorkoutStore/SET_LIVE_SESSION", { recoveryPromptedAt: Date.now() })
      return true
    },
    async navigate(target) {
      this.busy = true
      this.error = ""
      try {
        await this.$router.push(target)
        this.dialog = false
      } catch (_) {
        this.error = "Die Ansicht konnte nicht geöffnet werden. Bitte erneut versuchen."
      } finally { this.busy = false }
    },
    review() {
      trainingLiveService.prepareRecovery()
      return this.navigate({ name: "WorkoutSummary" })
    },
    resume() {
      if (trainingLiveService.resumeRecovered()) return this.navigate({ name: "Workout_run" })
      trainingLiveService.prepareRecovery()
      return this.navigate({ name: "WorkoutSummary", query: { resume: "true" } })
    },
    async discard() {
      if (this.busy || !this.info || this.pendingSave) return
      this.busy = true
      this.error = ""
      try {
        await this.$store.dispatch("runWorkoutStore/resetWorkoutState")
        this.dialog = false
      } catch (_) {
        this.error = "Das Training konnte nicht entfernt werden. Bitte erneut versuchen."
      } finally { this.busy = false }
    },
  },
  async mounted() {
    await this.$store.restored
    if (this._isDestroyed) return
    this.ready = true
    this.refresh()
    this.interval = setInterval(this.refresh, 60000)
    document.addEventListener("visibilitychange", this.refresh)
    window.addEventListener("trainingLiveForeground", this.refresh)
  },
  beforeDestroy() {
    clearInterval(this.interval)
    document.removeEventListener("visibilitychange", this.refresh)
    window.removeEventListener("trainingLiveForeground", this.refresh)
  },
}
</script>

<style scoped>
.recovery-notice {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 22px;
  color: #f3f8f5;
  text-align: left;
  border: 1px solid #35463e;
  border-radius: 22px;
  background: linear-gradient(115deg, #202e28, #1b2220 75%);
  box-shadow: 0 6px 22px rgba(0, 0, 0, .12);
  transition: border-color .18s, background-color .18s;
}
.recovery-notice:focus-visible, .recovery-dialog .v-btn:focus-visible {
  outline: 3px solid #8ce7bb;
  outline-offset: 3px;
}
.recovery-notice-copy { display: flex; min-width: 0; flex-direction: column; gap: 4px; }
.recovery-eyebrow { color: #a8cfb9; font-size: 11px; line-height: 1.5; letter-spacing: .09em; font-weight: 700; text-transform: uppercase; }
.recovery-notice-copy strong { font-size: 18px; line-height: 1.35; font-weight: 700; }
.recovery-plan-name { color: #dce5df; font-size: 14px; overflow-wrap: anywhere; }
.recovery-notice-meta { display: flex; flex-wrap: wrap; gap: 6px; color: #aabbb1; font-size: 12px; line-height: 1.6; }
.recovery-notice-action { display: flex; align-items: center; gap: 10px; color: #a8efca; font-size: 14px; font-weight: 700; }
.recovery-dialog {
  padding: 28px;
  border: 1px solid #35403a;
  border-radius: 24px !important;
  background: linear-gradient(155deg, #222d27, #191e1b 55%) !important;
  color: #f3f8f5;
}
.recovery-dialog-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.recovery-close { color: #b4c1b9 !important; }
.recovery-title { margin: 6px 0 10px; font-size: 27px; line-height: 1.2; letter-spacing: -.025em; font-weight: 750; }
.recovery-description { margin-bottom: 22px; color: #b5c2b9; font-size: 14px; line-height: 1.65; }
.recovery-workout { margin-bottom: 24px; padding: 18px; border: 1px solid #36423a; border-radius: 16px; background: rgba(255, 255, 255, .025); }
.recovery-workout-label { margin-bottom: 5px; color: #9dafaa; font-size: 12px; }
.recovery-workout h3 { margin-bottom: 12px; font-size: 17px; line-height: 1.45; overflow-wrap: anywhere; }
.recovery-workout-facts { display: flex; flex-wrap: wrap; gap: 10px 18px; color: #bdc9c2; font-size: 13px; }
.recovery-workout-facts span { display: flex; align-items: center; gap: 7px; }
.recovery-actions { display: grid; gap: 10px; }
.recovery-dialog .v-btn { border-radius: 13px; text-transform: none; letter-spacing: 0; font-size: 14px; font-weight: 650; }
.recovery-dialog .recovery-primary { color: #13251c !important; }
.recovery-dialog .recovery-secondary { color: #e3ece6; border-color: #48594e; background: rgba(255, 255, 255, .02); }
.recovery-quiet-actions { display: flex; align-items: center; justify-content: space-between; margin: 0 -10px -8px; }
.recovery-quiet-actions .v-btn { color: #aabbb1; }
@media (hover: hover) {
  .recovery-notice:hover { border-color: #64977b; }
}
@media (max-width: 480px) {
  .recovery-notice { grid-template-columns: minmax(0, 1fr); gap: 12px; padding: 18px; }
  .recovery-notice-copy strong { font-size: 17px; }
  .recovery-notice-action { grid-column: 1; justify-content: space-between; padding-top: 9px; border-top: 1px solid #35463e; }
  .recovery-dialog { padding: 22px; }
  .recovery-title { font-size: 25px; }
}
@media (prefers-reduced-motion: reduce) {
  .recovery-notice { transition: none; }
}
</style>

<style>
.v-dialog.recovery-dialog-overlay { margin: 16px; width: calc(100% - 32px); border-radius: 24px; }
</style>
